import { memoize } from 'lodash';
import { BehaviorSubject } from 'rxjs';

import { makeNamespacedLog } from '@sb/log';
import { wait } from '@sb/utilities';
import { API_ENDPOINT } from '@sbrc/utils';

const log = makeNamespacedLog('WsServerHandle');

export class WsServerHandle {
  protected ws: WebSocket | null = null;

  private shouldDisconnect = false;

  private connectionStatus = new BehaviorSubject<{ kind: string }>({
    kind: 'constructing',
  });

  public constructor(private readonly connectionPath: string) {
    this.connect();
  }

  public disconnect() {
    this.shouldDisconnect = true;
    this.ws?.close();
  }

  private connect() {
    // don't do anything when running on server
    if (typeof window === 'undefined' || this.shouldDisconnect) {
      return;
    }

    log.info(`uncategorized`, 'Connecting...');
    this.connectionStatus.next({ kind: 'connecting' });

    this.ws = new WebSocket(
      `${API_ENDPOINT}robby-server-api/${this.connectionPath}/`,
    );

    this.ws.binaryType = 'arraybuffer';

    const connectingTimeoutID = setTimeout(() => {
      if (this.ws?.readyState === WebSocket.CONNECTING) {
        log.info(`uncategorized`, 'Timed out while connecting');
        this.ws.close(4001, 'Connecting timeout');
      }
    }, 5_000);

    const destructors: Array<() => void> = [];

    // teardown is memoized so it is only called once
    const teardownAndReconnect = memoize(async () => {
      log.info(`uncategorized`, 'Teardown', {
        connectionState: this.ws?.readyState,
      });

      this.ws?.close();

      for (const destructor of destructors) {
        destructor();
      }

      if (this.getConnectionStatus().kind !== 'connecting') {
        this.connectionStatus.next({ kind: 'disconnected' });
      }

      if (!this.shouldDisconnect) {
        await wait(1000);
        this.connect();
      }
    });

    this.ws.onopen = () => {
      log.info(`uncategorized`, `${this.connectionPath} WS Server Connected`);
      clearTimeout(connectingTimeoutID);
      this.connectionStatus.next({ kind: 'connected' });
    };

    this.ws.onmessage = (event) => {
      try {
        const enc = new TextDecoder();
        const decodedData = enc.decode(event.data);
        const message = JSON.parse(decodedData);
        this.handleWsMessage(message);
      } catch (err) {
        log.error(`uncategorized`, 'Failed to parse', err);
      }
    };

    this.ws.onerror = (event) => {
      log.error(`uncategorized`, 'WS ERROR', event);
    };

    this.ws.onclose = (event) => {
      log.error(`uncategorized`, 'WS CLOSE', event);
      this.connectionStatus.next({ kind: 'disconnected' });
      teardownAndReconnect();
    };
  }

  protected handleWsMessage(_message: any) {
    // override in subclass
    log.warn(`uncategorized`, 'Unhandled message');
  }

  public getConnectionStatus(): { kind: string } {
    return this.connectionStatus.value;
  }

  public onConnectionChange(
    cb: (connectionStatus: { kind: string }) => void,
  ): () => void {
    const subscription = this.connectionStatus.subscribe(cb);

    return () => subscription.unsubscribe();
  }
}
